import type { ReceiveConfirmArrivalRequest, ReceiveConfirmContextDto, ReceiveConfirmLineDto } from '@/entities/receive';

import type { ConfirmArrivalFormValues, ConfirmArrivalLineValues } from '../model/types';
import { isLineEmpty } from './quantities';

/** Trims free text and drops it entirely when nothing is left. */
const optionalText = (value: string): string | undefined => {
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
};

/** Good is prefilled with the full outstanding expectation — a full receipt is one click. */
export const buildLineDefaults = (line: ReceiveConfirmLineDto): ConfirmArrivalLineValues => ({
  lineId: line.lineId,
  goodQty: line.expectedGoodQty,
  damagedQty: line.expectedDamagedQty,
  lostQty: 0
});

export const buildEmptyLineValues = (line: ReceiveConfirmLineDto): ConfirmArrivalLineValues => ({
  lineId: line.lineId,
  goodQty: 0,
  damagedQty: 0,
  lostQty: 0
});

/**
 * Form defaults from a freshly fetched context. The receipt date defaults to
 * `maxReceiveDate` (today per the backend) so it is always inside the allowed
 * window; the storage area is preselected only when the location offers exactly
 * one, which is also the case where the backend does not require a choice.
 */
export const buildFormDefaults = (context: ReceiveConfirmContextDto): ConfirmArrivalFormValues => ({
  targetLocationId: context.defaultTargetLocationId ?? '',
  targetLocationSpaceSeq: context.storageAreas.length === 1 ? context.storageAreas[0].seqno : null,
  actualDateOfReceipt: context.maxReceiveDate,
  comments: '',
  transportDocNumber: context.defaultTransportDocNumber ?? '',
  explanationOfLosses: '',
  printGoodsReceivedNote: false,
  lines: context.lines.map(buildLineDefaults)
});

/**
 * Form values → request body.
 *
 * All-zero lines are dropped rather than sent: the backend would skip them
 * anyway (`skippedLines`), and omitting them keeps the payload honest about what
 * was actually received. A body whose every line is zero still submits — the
 * backend answers `200` with nothing written, which is the correct "I received
 * none of it" outcome.
 *
 * `receiveAndRelease` is never set: the flag performs no release, it only asks
 * for a prefill of a release screen this app does not have yet (RUIF-0005).
 */
export const buildConfirmArrivalBody = (
  values: ConfirmArrivalFormValues,
  context: ReceiveConfirmContextDto
): ReceiveConfirmArrivalRequest => {
  const lines = values.lines.filter((line) => !isLineEmpty(line));
  const hasLoss = lines.some((line) => line.lostQty > 0);
  const storageAreaExists = context.storageAreas.some((area) => area.seqno === values.targetLocationSpaceSeq);

  return {
    targetLocationId: values.targetLocationId,
    ...(values.targetLocationSpaceSeq != null && storageAreaExists
      ? { targetLocationSpaceSeq: values.targetLocationSpaceSeq }
      : {}),
    actualDateOfReceipt: values.actualDateOfReceipt,
    comments: optionalText(values.comments),
    transportDocNumber: optionalText(values.transportDocNumber),
    // Only meaningful alongside a loss; the backend requires it exactly then.
    explanationOfLosses: hasLoss ? optionalText(values.explanationOfLosses) : undefined,
    lines: lines.map(({ lineId, goodQty, damagedQty, lostQty }) => ({
      lineId,
      goodQty,
      damagedQty,
      lostQty
    })),
    printGoodsReceivedNote: values.printGoodsReceivedNote
  };
};
